// PLAT-2857 — owns the Collect submit action switch; the optional Plaid upgrade loop is shared
// with the Confirm flow via useSccPlaidUpgrade.
//
// The request always terminalizes at submit (success or blocked). 'open_plaid' means the request
// completed as CertifID-with-ownership-not-validated and the participant may optionally link
// their bank to upgrade it; every Plaid outcome — match, mismatch, exit, error — therefore ends
// on the same success screen via advanceFlow(). The modal lives on CollectReview.
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { finalizeCollectRequestApi } from '@Api/requestFlow.api';
import type { FinalizeCollectRequestDto } from '@Types/FinalizeCollectRequest.interface';
import useFlowNavigation from '@Use/useFlowNavigation';
import useSccPlaidUpgrade from '@Use/useSccPlaidUpgrade';

export default function useCollectSubmit() {
    const router = useRouter();
    const { advanceFlow } = useFlowNavigation();
    const {
        plaidLinkToken,
        isPlaidModalOpen,
        isPrimingPlaid,
        isCompletingPlaid,
        offerPlaid,
        continueToPlaid,
        onPlaidSuccess,
        onPlaidExit,
    } = useSccPlaidUpgrade(advanceFlow);

    const isSubmitting = ref(false);
    const errorMessage = ref<string | null>(null);

    const submit = async (dto: FinalizeCollectRequestDto): Promise<void> => {
        if (isSubmitting.value) return;
        isSubmitting.value = true;
        errorMessage.value = null;

        try {
            const result = await finalizeCollectRequestApi(dto);
            // A missing body/action means a pre-PLAT-2857 BFF — the legacy success outcome.
            const action = result?.action ?? 'success';

            if (action === 'blocked') {
                await router.push({
                    name: 'request-blocked',
                    params: router.currentRoute.value.params,
                    query: router.currentRoute.value.query,
                });
                return;
            }

            if (action === 'open_plaid' && result?.plaidLinkToken) {
                // PLAT-3131: the offer opens the priming step, not Plaid itself.
                offerPlaid(result.plaidLinkToken);
                // isSubmitting stays true — the flow advances when the Plaid modal resolves.
                return;
            }

            advanceFlow();
        } catch {
            errorMessage.value = 'Something went wrong. Please try again.';
            isSubmitting.value = false;
        }
    };

    return {
        isSubmitting,
        errorMessage,
        plaidLinkToken,
        isPlaidModalOpen,
        isPrimingPlaid,
        isCompletingPlaid,
        submit,
        continueToPlaid,
        onPlaidSuccess,
        onPlaidExit,
    };
}
